import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import {
  GridColDef,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import ScrollArea from '@/shared/ui/ScrollArea';

import { ListViewRowSelection } from './GeneratikTable.types';
import { sortRowsBySortModel, sumGroupItemPrice } from './GeneratikTable.utils';
import GeneratikTableErrorOverlay from './GeneratikTableErrorOverlay';
import { groupRowsContainerSx } from './GeneratikTableListView.styles';
import GeneratikTableListViewGroupHeader from './GeneratikTableListViewGroupHeader';
import { GroupOption } from './GeneratikTableToolbar.types';
import useGeneratikTableStore from './generatikTableStore';
import useTableScrollMemory from './hooks/useTableScrollMemory';

export interface GeneratikTableListViewProps<R extends GridValidRowModel = GridValidRowModel> {
  tableId?: string;
  rows: R[];
  renderListViewRow: (
    row: R,
    openFilters: () => void,
    selection: ListViewRowSelection,
  ) => ReactNode;
  openFilters: () => void;
  getRowId?: (row: R) => string | number;
  emptyStateMessage?: string;
  /** Renders the error state instead of the empty state when the query failed. */
  isError?: boolean;
  onRetry?: () => void;
  loading?: boolean;
  selectionModel: GridRowSelectionModel;
  onToggleRowSelection: (rowId: GridRowId) => void;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  scrollable?: boolean;
  groupByField?: string | null;
  groupOptions?: GroupOption[];
  getGroupItemPrice?: (row: R) => number | undefined;
  currency?: string;
  priceForPeriod?: boolean;
  isRowSelectable?: (params: GridRowParams<R>) => boolean;
  isChildRow?: (row: R) => boolean;
  sortModel?: GridSortModel;
  sortColumns?: GridColDef<R>[];
}

const ListContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'scrollable' && prop !== 'grouped',
})<{ scrollable?: boolean; grouped?: boolean }>(({ theme, scrollable, grouped }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  gap: '8px',
  background: scrollable ? 'transparent' : theme.palette.grey[50],
  borderRadius: scrollable ? 0 : '14px',
  ...(grouped && { marginTop: '-0.5rem' }),
}));

const EmptyState = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '1rem',
  backgroundColor: '#F7F7F8',
  borderRadius: '0.5rem',
  margin: '1rem',
});

const GeneratikTableListView = <R extends GridValidRowModel = GridValidRowModel>({
  tableId,
  rows,
  renderListViewRow,
  openFilters,
  getRowId,
  emptyStateMessage,
  isError = false,
  onRetry,
  loading = false,
  selectionModel,
  onToggleRowSelection,
  onRowSelectionModelChange,
  scrollable = true,
  groupByField,
  groupOptions,
  getGroupItemPrice,
  currency,
  priceForPeriod,
  isRowSelectable,
  isChildRow,
  sortModel,
  sortColumns,
}: GeneratikTableListViewProps<R>) => {
  const { t } = useTranslation();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () =>
      new Set(
        tableId ? (useGeneratikTableStore.getState().collapsedListGroups[tableId] ?? []) : [],
      ),
  );

  useEffect(() => {
    if (!tableId) return;
    useGeneratikTableStore.getState().setCollapsedListGroups(tableId, Array.from(collapsedGroups));
  }, [tableId, collapsedGroups]);

  const previousGroupByFieldRef = useRef(groupByField);
  useEffect(() => {
    if (previousGroupByFieldRef.current === groupByField) return;
    previousGroupByFieldRef.current = groupByField;
    setCollapsedGroups((previous) => (previous.size === 0 ? previous : new Set()));
  }, [groupByField]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useTableScrollMemory({
    tableId: scrollable ? tableId : undefined,
    view: 'list',
    getScroller: () => scrollRef.current,
    ready: rows.length > 0,
  });

  const resolveRowId = useCallback(
    (row: R): string | number => {
      if (getRowId) return getRowId(row);
      return (row as unknown as { id: string | number }).id;
    },
    [getRowId],
  );

  const selectedIdSet = useMemo(() => new Set(selectionModel.map(String)), [selectionModel]);

  const activeGroupOption = useMemo(
    () => groupOptions?.find((o) => o.field === groupByField),
    [groupOptions, groupByField],
  );

  const sortRows = useCallback(
    (rowsToSort: R[]): R[] =>
      sortModel?.length && sortColumns
        ? sortRowsBySortModel(rowsToSort, sortModel, sortColumns)
        : rowsToSort,
    [sortModel, sortColumns],
  );

  const groups = useMemo(() => {
    if (!activeGroupOption?.getRowValue) return null;

    const map = new Map<string, R[]>();
    for (const row of rows) {
      if (isChildRow?.(row)) continue;
      const key = activeGroupOption.getRowValue(row) || t('group_other');
      const existing = map.get(key);
      if (existing) {
        existing.push(row);
      } else {
        map.set(key, [row]);
      }
    }

    for (const [key, groupRows] of map) {
      map.set(key, sortRows(groupRows));
    }

    return map;
  }, [rows, activeGroupOption, isChildRow, t, sortRows]);

  const sortedUngroupedRows = useMemo(() => sortRows(rows), [rows, sortRows]);

  const toggleGroup = useCallback((label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }, []);

  const renderRow = (row: R) => {
    const rowId = resolveRowId(row);
    const selection: ListViewRowSelection = {
      isSelected: selectedIdSet.has(String(rowId)),
      toggleSelected: () => onToggleRowSelection(rowId),
      isSelectedById: (id: string) => selectedIdSet.has(id),
      toggleById: (id: string) => onToggleRowSelection(id),
    };
    const content = renderListViewRow(row, openFilters, selection);
    if (!content) return null;
    return <Box key={rowId}>{content}</Box>;
  };

  // Loading/error/empty must render inside the same ListContainer/ScrollArea
  // as populated rows: an early return here would unmount the scroll container
  // whenever rows briefly empty (e.g. a refetch without keepPreviousData) and
  // drop the list's scroll position.
  let children: ReactNode;
  if (!rows.length) {
    if (loading) {
      children = (
        <EmptyState data-testid="list-view-loading">
          <Typography variant="body1">{t('Loading...')}</Typography>
        </EmptyState>
      );
    } else if (isError) {
      children = <GeneratikTableErrorOverlay onRetry={onRetry} />;
    } else {
      children = (
        <EmptyState data-testid="list-view-empty-state">
          <Typography variant="body1">{emptyStateMessage || t('No results found')}</Typography>
        </EmptyState>
      );
    }
  } else {
    children = groups
      ? Array.from(groups.entries()).map(([label, groupRows]) => {
          const expanded = !collapsedGroups.has(label);
          const selectedCount = groupRows.filter((row) =>
            selectedIdSet.has(String(resolveRowId(row))),
          ).length;
          const totalPrice = getGroupItemPrice
            ? sumGroupItemPrice(groupRows, getGroupItemPrice)
            : undefined;
          const selectedPrice =
            getGroupItemPrice && selectedCount > 0
              ? sumGroupItemPrice(
                  groupRows.filter((row) => selectedIdSet.has(String(resolveRowId(row)))),
                  getGroupItemPrice,
                )
              : undefined;

          const allGroupSelected = groupRows.every((row) =>
            selectedIdSet.has(String(resolveRowId(row))),
          );
          const groupCheckboxDisabled =
            isRowSelectable !== undefined &&
            groupRows.every(
              (row) => !isRowSelectable({ row, id: resolveRowId(row) } as GridRowParams<R>),
            );
          const handleToggleGroupRows = () => {
            if (!onRowSelectionModelChange) return;
            const groupRowIds = groupRows.map((row) => resolveRowId(row));
            if (allGroupSelected) {
              const idsToRemove = new Set(groupRowIds.map(String));
              onRowSelectionModelChange(
                selectionModel.filter((id) => !idsToRemove.has(String(id))),
              );
            } else {
              const existingIds = new Set(selectionModel.map(String));
              const idsToAdd = groupRowIds.filter((id) => !existingIds.has(String(id)));
              onRowSelectionModelChange([...selectionModel, ...idsToAdd]);
            }
          };

          return (
            <Box key={label}>
              <GeneratikTableListViewGroupHeader
                label={label}
                totalCount={groupRows.length}
                selectedCount={selectedCount}
                selectedPrice={selectedPrice}
                totalPrice={totalPrice}
                currency={currency}
                priceForPeriod={priceForPeriod}
                expanded={expanded}
                onToggle={() => toggleGroup(label)}
                onToggleGroupSelection={handleToggleGroupRows}
                checkboxDisabled={groupCheckboxDisabled}
              />
              {expanded && <Box sx={groupRowsContainerSx}>{groupRows.map(renderRow)}</Box>}
            </Box>
          );
        })
      : sortedUngroupedRows.map(renderRow);
  }

  const content = (
    <ListContainer scrollable={scrollable} grouped={!!rows.length && !!groups}>
      {children}
    </ListContainer>
  );

  return scrollable ? <ScrollArea ref={scrollRef}>{content}</ScrollArea> : content;
};

export default GeneratikTableListView;
